// ==========================================================================
// Loan Offering Hook - Manages modal/banner display timing and state
// Relies on backend `seen` flag as source of truth (no client-side storage)
// ==========================================================================

import { useEffect, useRef, useState, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { acknowledgeLoanOffering } from '@/services/loanOffering.service';
import { alertKeys } from '@/hooks/useAlert';
import type { LoanOffering } from '@/types/alert.types';

/**
 * Hook to manage loan offering display logic.
 *
 * - First time (seen=false): shows modal after 2s delay
 * - Subsequent (seen=true): shows banner immediately
 * - Cancels timers if user navigates away (isVisible = false)
 * - Calls acknowledge endpoint when modal first displays
 * - After dismiss: shows banner for rest of session (local state only)
 */
export function useLoanOffering(loanOffering: LoanOffering | undefined, isVisible: boolean, alertId?: number) {
  const [showModal, setShowModal] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const acknowledgedRef = useRef(false);
  const prevAlertIdRef = useRef(alertId);

  const queryClient = useQueryClient();

  const acknowledgeMutation = useMutation({
    mutationFn: acknowledgeLoanOffering,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: alertKeys.all });
    },
  });

  const eligible = loanOffering?.eligible ?? false;
  const seen = loanOffering?.seen ?? false;

  // Reset state when alert ID changes (new alert)
  useEffect(() => {
    if (alertId !== prevAlertIdRef.current) {
      prevAlertIdRef.current = alertId;
      setShowModal(false);
      setDismissed(false);
      acknowledgedRef.current = false;
    }
  }, [alertId]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const acknowledge = useCallback(() => {
    if (acknowledgedRef.current) return;
    acknowledgedRef.current = true;
    acknowledgeMutation.mutate();
  }, [acknowledgeMutation]);

  const dismissModal = useCallback(() => {
    setShowModal(false);
    setDismissed(true);
  }, []);

  useEffect(() => {
    if (!eligible || !isVisible) {
      clearTimer();
      setShowModal(false);
      return;
    }

    // Already seen (BE flag) or dismissed this session → banner only
    if (seen || dismissed) {
      return;
    }

    // Not yet seen → start timer for modal
    if (!showModal) {
      clearTimer();
      timerRef.current = setTimeout(() => {
        setShowModal(true);
      }, 2_000);
    }

    return () => clearTimer();
  }, [eligible, seen, dismissed, isVisible, clearTimer, showModal]);

  // Call acknowledge when modal first appears
  useEffect(() => {
    if (showModal && !acknowledgedRef.current) {
      acknowledge();
    }
  }, [showModal, acknowledge]);

  const offeringType = loanOffering?.type ?? 'ussd';
  const value = loanOffering?.value ?? '';
  const telLink = offeringType === 'ussd' && value ? `tel:${encodeUSSD(value)}` : undefined;

  return {
    showModal: showModal && isVisible && eligible,
    showBanner: (seen || dismissed) && !showModal && isVisible && eligible,
    dismissModal,
    offeringType,
    telLink,
    value,
    title: loanOffering?.title ?? '',
    body: loanOffering?.body ?? '',
  };
}

/** Encode USSD code for tel: URI (# -> %23) */
function encodeUSSD(code: string): string {
  return code.replace(/#/g, '%23');
}
